/**
 * Collecting and searching for browser-level cookies.
 *
 * @author Jaskiran Chhokar
 */

import browserCookieNames from '../constants/browserCookieNames';
import { ResponseBody } from 'globalTypes';
import { transformCookieNames, transformCookieToObj } from '../utils/helpers/formatCookies';

/**
 * Retrieves the value of a specified cookie from the browser's cookies
 *
 * @function
 * @name getCookieValue
 * @param {string} cookieName - The name of the cookie to retrieve.
 * @return {string} - The value of the cookie if found, otherwise an empty string.
 * @example getCookieValue(uID) returns 28a39042-ffc4-4387-9644-95957fcdd1b4 (if the cookie exists)
 * @description
 * This function decodes the document's cookies, splits them into individual cookies,
 * and searches for the specified cookie by name. If found, it extracts and returns
 * its value. If not found it returns an empty string.
 *
 * @author Jaskiran Chhokar
 */
const getCookieValue = (cookieName: string) => {
    cookieName = cookieName + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookies = decodedCookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i];
        while (cookie.charAt(0) == ' ') {
            cookie = cookie.substring(1);
        }
        if (cookie.indexOf(cookieName) == 0) {
            return cookie.substring(cookieName.length, cookie.length);
        }
    }
    return '';
};

/**
 * Captures specified cookies from the browser and adds them to the user profile data.
 *
 * @function
 * @name captureCookies
 * @param {ResponseBody} userProfileData - The user profile data where the cookies will be stored.
 * @returns {Object} - The updated user profile data with captured cookies.
 *
 * @description
 * This function iterates over a predefined list of cookie names (`browserCookieNames`).
 * For each cookie, it attempts to retrieve its value using `getCookieValue()`.
 * If the value is not empty, it adds the cookie name and value to the `allCookiesData` object.
 * Finally, it merges the collected cookies into the `userProfileData` and returns the updated object.
 */
const captureCookies = (userProfileData: ResponseBody) => {
    let allCookiesData: { [key: string]: string } = {};
    browserCookieNames.forEach((cookieName: string) => {
        if (getCookieValue(cookieName) !== '') {
            allCookiesData[`${transformCookieNames(cookieName)}`] = getCookieValue(cookieName);
        }
    });
    return Object.assign(userProfileData.universalIds, allCookiesData);
};

/**
 * Captures OptanonConsent cookies value from the browser and adds them to the user profile data.
 *
 * @function
 * @name captureConsentCookies
 * @param {ResponseBody} userProfileData - The user profile data where the cookies will be stored.
 * @returns {Object} - The updated user profile data with captured cookies.
 *
 */

const captureConsentCookies = (userProfileData: ResponseBody) => {
    let allCookiesData: { [key: string]: string | boolean } = {};
    const cookieValue = transformCookieToObj(getCookieValue('OptanonConsent'));
    if (cookieValue) {
        allCookiesData.consentTimestamp = cookieValue?.date;
        allCookiesData.functionalConsentOptIn = cookieValue?.groups?.C0003;
        allCookiesData.performanceConsentOptIn = cookieValue?.groups?.C0002;
        allCookiesData.marketingConsentOptIn = cookieValue?.groups?.C0004;
        allCookiesData.optInValues = cookieValue?.output;
    }
    return Object.assign(userProfileData.consent, allCookiesData);
};

export { captureCookies, getCookieValue, captureConsentCookies };
